import { FC, useEffect, useState } from "react";

import { ContentCopy, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  deleteSsoConnection,
  getSsoJit,
  listSsoConnections,
  patchSsoSamlConnection,
  postSsoSamlConnection,
  putSsoConnectionJit,
  type GetSsoJitResponse,
} from "@rubi/api-generated";
import { useTranslation } from "react-i18next";

// View-shape for the fields this panel reads off a SAML connection. The
// generated `saml_connections` entries are typed as `{ [key: string]: unknown }`
// because Stytch's response is pass-through — narrow at the read boundary.
type SamlConnection = {
  organization_id?: string;
  connection_id: string;
  status?: string;
  display_name: string;
  identity_provider?: string;
  idp_entity_id: string;
  idp_sso_url: string;
  acs_url: string;
  audience_uri: string;
  attribute_mapping?: Record<string, unknown> | null;
};

// The only four values Stytch accepts for identity_provider — anything else is
// rejected with a 400 `invalid_sso_idp`. This list previously offered OneLogin,
// JumpCloud, PingFederate and Keycloak, none of which Stytch models; those IdPs
// wire up fine as "Generic SAML". Entra is first since it's the primary use case.
const IDENTITY_PROVIDERS: { value: string; labelKey: string }[] = [
  { value: "microsoft-entra", labelKey: "identityProviders.microsoftEntra" },
  { value: "okta", labelKey: "identityProviders.okta" },
  { value: "google-workspace", labelKey: "identityProviders.googleWorkspace" },
  { value: "generic", labelKey: "identityProviders.generic" },
];

/**
 * api-rs puts the real reason in the response body's `console_msg`; an
 * AxiosError's own `message` is only "Request failed with status code NNN", which
 * is how a Stytch `invalid_sso_idp` error stayed invisible here.
 */
const errorMessage = (error: unknown, fallback: string): string => {
  const consoleMsg = (
    error as { response?: { data?: { console_msg?: unknown } } } | null
  )?.response?.data?.console_msg;
  if (typeof consoleMsg === "string" && consoleMsg) return consoleMsg;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

export const SsoConnectionsPanel: FC<{ orgId: string }> = ({ orgId }) => {
  const { t } = useTranslation("dev/sso");
  const [connections, setConnections] = useState<SamlConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [jit, setJit] = useState<GetSsoJitResponse | null>(null);
  const [jitError, setJitError] = useState<string | null>(null);

  // Create form
  const [displayName, setDisplayName] = useState("");
  const [provider, setProvider] = useState(IDENTITY_PROVIDERS[0].value);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<SamlConnection | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await listSsoConnections({ query: { org_id: orgId } });
      setConnections(
        response.data!.saml_connections as unknown as SamlConnection[],
      );
      setLoadError(null);
    } catch (e) {
      setLoadError(errorMessage(e, t("errors.loadFailed")));
    } finally {
      setLoading(false);
    }

    // A backend without /sso/jit disables the JIT controls rather than hiding the
    // connections.
    try {
      const response = await getSsoJit({ query: { org_id: orgId } });
      setJit(response.data!);
      setJitError(null);
    } catch (e) {
      setJit(null);
      setJitError(errorMessage(e, t("errors.jitLoadFailed")));
    }
  };

  /** The server amends the allow-list, so we never send the whole set. */
  const handleJitToggle = async (connectionId: string, allowed: boolean) => {
    const response = await putSsoConnectionJit({
      path: { connection_id: connectionId },
      body: { org_id: orgId, allowed },
    });
    setJit(response.data!);
  };

  useEffect(() => {
    load();
  }, [orgId]);

  const handleCreate = async () => {
    if (!displayName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      await postSsoSamlConnection({
        body: {
          org_id: orgId,
          display_name: displayName.trim(),
          identity_provider: provider,
        },
      });
      setDisplayName("");
      await load();
    } catch (e) {
      setCreateError(errorMessage(e, t("errors.createFailed")));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSsoConnection({
        path: { connection_id: deleteTarget.connection_id },
        query: { org_id: orgId },
      });
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setLoadError(errorMessage(e, t("errors.deleteFailed")));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t("createConnection.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("createConnection.description")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label={t("createConnection.displayName")}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            label={t("createConnection.identityProvider")}
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            sx={{ minWidth: 240 }}
          >
            {IDENTITY_PROVIDERS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {t(p.labelKey)}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || displayName.trim() === ""}
          >
            {creating
              ? t("createConnection.submitting")
              : t("createConnection.submit")}
          </Button>
        </Stack>
        {createError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {createError}
          </Alert>
        )}
      </Paper>

      <Box>
        <Typography variant="h6" gutterBottom>
          {t("list.title")}
        </Typography>
        {loadError && <Alert severity="error">{loadError}</Alert>}
        {loading && (
          <Typography variant="body2">{t("list.loading")}</Typography>
        )}
        {!loading && connections.length === 0 && !loadError && (
          <Typography variant="body2" color="text.secondary">
            {t("list.empty")}
          </Typography>
        )}
        <Stack spacing={1}>
          {connections.map((c) => (
            <ConnectionRow
              key={c.connection_id}
              connection={c}
              orgId={orgId}
              jit={jit}
              jitDisabledReason={jitError}
              onJitToggle={handleJitToggle}
              onChange={load}
              onRequestDelete={() => setDeleteTarget(c)}
            />
          ))}
        </Stack>
      </Box>

      <Dialog open={deleteTarget != null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t("deleteDialog.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("deleteDialog.body")}</DialogContentText>
          <Typography variant="caption" component="div" sx={{ mt: 1 }}>
            {deleteTarget?.display_name} ({deleteTarget?.connection_id})
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t("deleteDialog.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting
              ? t("deleteDialog.confirming")
              : t("deleteDialog.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

const ConnectionRow: FC<{
  connection: SamlConnection;
  orgId: string;
  jit: GetSsoJitResponse | null;
  jitDisabledReason: string | null;
  onJitToggle: (connectionId: string, allowed: boolean) => Promise<void>;
  onChange: () => void;
  onRequestDelete: () => void;
}> = ({
  connection,
  orgId,
  jit,
  jitDisabledReason,
  onJitToggle,
  onChange,
  onRequestDelete,
}) => {
  const { t } = useTranslation("dev/sso");
  const [metadataUrl, setMetadataUrl] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [displayName, setDisplayName] = useState(connection.display_name);
  const [idpEntityId, setIdpEntityId] = useState(connection.idp_entity_id);
  const [idpSsoUrl, setIdpSsoUrl] = useState(connection.idp_sso_url);
  const [x509, setX509] = useState("");
  const [attributeMapping, setAttributeMapping] = useState(
    connection.attribute_mapping
      ? JSON.stringify(connection.attribute_mapping, null, 2)
      : "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jitBusy, setJitBusy] = useState(false);

  const jitAllowed =
    jit?.sso_jit_provisioning_allowed_connections.includes(
      connection.connection_id,
    ) ?? false;

  const isActive = connection.status?.toLowerCase() === "active";

  const submitMetadataUrl = async () => {
    if (!metadataUrl.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await patchSsoSamlConnection({
        path: { connection_id: connection.connection_id },
        body: {
          org_id: orgId,
          metadata_url: metadataUrl.trim(),
        },
      });
      setMetadataUrl("");
      onChange();
    } catch (e) {
      setError(errorMessage(e, t("errors.metadataUrlFailed")));
    } finally {
      setBusy(false);
    }
  };

  const submitManual = async () => {
    setBusy(true);
    setError(null);
    try {
      await patchSsoSamlConnection({
        path: { connection_id: connection.connection_id },
        body: {
          org_id: orgId,
          display_name:
            displayName !== connection.display_name ? displayName : null,
          idp_entity_id:
            idpEntityId !== connection.idp_entity_id ? idpEntityId : null,
          idp_sso_url: idpSsoUrl !== connection.idp_sso_url ? idpSsoUrl : null,
          x509_certificate: x509.trim() ? x509 : null,
        },
      });
      setX509("");
      onChange();
    } catch (e) {
      setError(errorMessage(e, t("errors.updateFailed")));
    } finally {
      setBusy(false);
    }
  };

  /** Its own action because the API rejects `metadata_url` plus manual fields. */
  const submitAttributeMapping = async () => {
    let parsed: Record<string, unknown>;
    try {
      // Empty clears it: Stytch treats {} as no mapping.
      parsed =
        attributeMapping.trim() === "" ? {} : JSON.parse(attributeMapping);
    } catch {
      setError(t("errors.mappingInvalidJson"));
      return;
    }
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      setError(t("errors.mappingNotObject"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await patchSsoSamlConnection({
        path: { connection_id: connection.connection_id },
        body: { org_id: orgId, attribute_mapping: parsed },
      });
      onChange();
    } catch (e) {
      setError(errorMessage(e, t("errors.mappingSaveFailed")));
    } finally {
      setBusy(false);
    }
  };

  const toggleJit = async (allowed: boolean) => {
    setJitBusy(true);
    setError(null);
    try {
      await onJitToggle(connection.connection_id, allowed);
    } catch (e) {
      setError(errorMessage(e, t("errors.jitUpdateFailed")));
    } finally {
      setJitBusy(false);
    }
  };

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <Typography sx={{ flex: 1 }}>{connection.display_name}</Typography>
          <Chip
            size="small"
            label={connection.identity_provider || "generic"}
          />
          <Chip
            size="small"
            color={isActive ? "success" : "default"}
            label={connection.status || "pending"}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Typography variant="subtitle2">
            {t("connection.idpInstructions")}
          </Typography>
          <CopyableField
            label={t("connection.acsUrl")}
            value={connection.acs_url}
          />
          <CopyableField
            label={t("connection.audienceUri")}
            value={connection.audience_uri}
          />
          <CopyableField
            label={t("connection.connectionId")}
            value={connection.connection_id}
          />

          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t("finishSetup.title")}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="p">
              {t("finishSetup.help")}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                size="small"
                label={t("finishSetup.metadataUrl")}
                value={metadataUrl}
                onChange={(e) => setMetadataUrl(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={submitMetadataUrl}
                disabled={busy || !metadataUrl.trim()}
              >
                {t("finishSetup.apply")}
              </Button>
            </Stack>
          </Box>

          <Box>
            <Button size="small" onClick={() => setManualOpen((v) => !v)}>
              {manualOpen ? t("manualConfig.hide") : t("manualConfig.show")}
            </Button>
            {manualOpen && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label={t("manualConfig.displayName")}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t("manualConfig.idpEntityId")}
                  value={idpEntityId}
                  onChange={(e) => setIdpEntityId(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t("manualConfig.idpSsoUrl")}
                  value={idpSsoUrl}
                  onChange={(e) => setIdpSsoUrl(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t("manualConfig.x509")}
                  value={x509}
                  onChange={(e) => setX509(e.target.value)}
                  fullWidth
                  multiline
                  minRows={4}
                  size="small"
                  helperText={t("manualConfig.x509Helper")}
                  inputProps={{
                    style: { fontFamily: "monospace", fontSize: 12 },
                  }}
                />
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={submitManual}
                    disabled={busy}
                  >
                    {t("manualConfig.save")}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t("attributeMapping.title")}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="p">
              {t("attributeMapping.helpBeforeExample")}{" "}
              <code>{'{ "email": "NameID", "first_name": "givenName" }'}</code>.{" "}
              {t("attributeMapping.helpAfterExample")}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              size="small"
              label={t("attributeMapping.label")}
              value={attributeMapping}
              onChange={(e) => setAttributeMapping(e.target.value)}
              sx={{ mt: 1 }}
              inputProps={{
                style: { fontFamily: "monospace", fontSize: 12 },
              }}
            />
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button
                variant="contained"
                onClick={submitAttributeMapping}
                disabled={busy}
              >
                {t("attributeMapping.save")}
              </Button>
            </Stack>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={jitAllowed}
                  disabled={jitBusy || jit == null}
                  onChange={(e) => toggleJit(e.target.checked)}
                />
              }
              label={t("jit.allow")}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
            >
              {jitDisabledReason
                ? jitDisabledReason
                : jit
                  ? t("jit.orgMode", {
                      mode: jit.sso_jit_provisioning,
                      connections:
                        jit.sso_jit_provisioning_allowed_connections.length,
                    })
                  : t("jit.loading")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
            >
              {t("jit.explanation")}
            </Typography>
          </Box>

          <Stack direction="row" justifyContent="flex-end">
            <Button color="error" onClick={onRequestDelete} disabled={busy}>
              {t("connection.delete")}
            </Button>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const CopyableField: FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  const { t } = useTranslation("dev/sso");
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        label={label}
        value={value ?? ""}
        size="small"
        fullWidth
        InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }}
      />
      <IconButton
        size="small"
        onClick={() => navigator.clipboard.writeText(value ?? "")}
        title={t("connection.copy")}
      >
        <ContentCopy fontSize="small" />
      </IconButton>
    </Stack>
  );
};
